// muiColorsOverrideHandler.ts
// Kept in sync 1:1 with varisht-admin/src/design-system/muiColorsOverrideHandler.ts (visual parity).
import { createTheme } from '@mui/material/styles';

// Brand
const blurpleMain = '#3a36b8';
const blurpleDark = '#2621b7';
const blurpleLight = '#5d59d4'; // lighter accent for dark surfaces
const blurpleSoft = '#EEF0FF'; // light tint for selected rows / hover

// Surfaces
const neutralBg = '#F5F6FB'; // page background
const paperBg = '#FFFFFF';
const borderSoft = '#E3E5F0';

// Text
const textPrimary = '#14152B';
const textSecondary = '#5B6080';
const textMuted = '#8A90A8';

// Sidebar (dark, Mira-style)
const sidebarBg = '#233044';
const sidebarBorder = 'rgba(255, 255, 255, 0.08)';
const sidebarText = '#A8B0C2';
const sidebarTextStrong = '#FFFFFF';
const sidebarIcon = '#8B95AB';
// Mira-style active row: slightly lifted surface on navy (not a white inset pill)
const sidebarSelectedBg = 'rgba(255, 255, 255, 0.08)';
const sidebarSectionLabel = 'rgba(168, 176, 194, 0.72)';

const headingFamily =
  '"Roboto Slab", "Roboto", "Georgia", "Times New Roman", serif';
const bodyFamily =
  '"Inter", system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif';

/** Scoped to `NavigationDrawer` paper only — avoids forcing sidebar colors on every ListItemButton (e.g. RBAC role list). */
export const navigationDrawerNestedSx = {
  '& .MuiListSubheader-root': {
    backgroundColor: 'transparent',
    color: sidebarSectionLabel,
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    lineHeight: 1.25,
    paddingLeft: '16px',
    paddingRight: '16px',
    paddingTop: '8px',
    paddingBottom: '4px',
  },
  '& .MuiListItemButton-root': {
    borderRadius: 0,
    marginInline: 0,
    marginBlock: 0,
    minHeight: 36,
    paddingTop: '6px',
    paddingBottom: '6px',
    paddingInline: '14px',
    alignItems: 'center',
    color: sidebarText,
    '& .MuiListItemText-root': {
      marginTop: 0,
      marginBottom: 0,
    },
    '& .MuiListItemText-primary': {
      fontSize: 13,
      fontWeight: 500,
      lineHeight: 1.25,
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.06)',
      color: sidebarTextStrong,
      '& .MuiListItemIcon-root': {
        color: sidebarTextStrong,
      },
    },
    '&.Mui-selected': {
      backgroundColor: sidebarSelectedBg,
      color: sidebarTextStrong,
      '&:hover': {
        backgroundColor: sidebarSelectedBg,
      },
      '& .MuiListItemIcon-root, & .MuiListItemText-primary': {
        color: sidebarTextStrong,
      },
      '& .MuiListItemText-primary': {
        fontWeight: 600,
      },
    },
    '& .MuiSvgIcon-root': {
      fontSize: '1.125rem',
    },
  },
  '& .MuiListItemIcon-root': {
    color: sidebarIcon,
    minWidth: 36,
    paddingTop: 0,
    paddingBottom: 0,
  },
};

/** Dark shell for `NavigationDrawer` paper only — default `MuiDrawer` uses app background so slide-over modals match main content. */
export const navigationDrawerPaperSx = {
  backgroundColor: sidebarBg,
  color: sidebarText,
  borderRight: `1px solid ${sidebarBorder}`,
  backgroundImage: 'none' as const,
};

export const muiColorsOverrideHandler = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: blurpleMain,
      light: blurpleLight,
      dark: blurpleDark,
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#06B6D4',
      light: '#7BDDEA',
      dark: '#03829A',
      contrastText: '#FFFFFF',
    },
    background: {
      default: neutralBg,
      paper: paperBg,
    },
    text: {
      primary: textPrimary,
      secondary: textSecondary,
    },
    divider: borderSoft,
  },

  shape: {
    borderRadius: 8,
  },

  typography: {
    fontFamily: bodyFamily,
    h1: { fontFamily: headingFamily, fontWeight: 600 },
    h2: { fontFamily: headingFamily, fontWeight: 600 },
    h3: { fontFamily: headingFamily, fontWeight: 600 },
    h4: { fontFamily: headingFamily, fontWeight: 600 },
    h5: { fontFamily: headingFamily, fontWeight: 600 },
    h6: { fontFamily: headingFamily, fontWeight: 600 },
    subtitle1: { fontWeight: 600 },
    subtitle2: { fontWeight: 600 },
    button: {
      textTransform: 'none',
      fontWeight: 600,
    },
  },

  components: {
    // Top app bar — plain white, subtle bottom border
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
        color: 'inherit',
      },
      styleOverrides: {
        root: {
          backgroundColor: paperBg,
          color: textPrimary,
          boxShadow: 'none',
          borderBottom: `1px solid ${borderSoft}`,
        },
      },
    },

    MuiToolbar: {
      styleOverrides: {
        root: {
          minHeight: 56,
          '@media (min-width: 600px)': {
            minHeight: 56,
          },
        },
      },
    },

    // Buttons — 8px radius, restrained shadows
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 8,
          paddingInline: 14,
          paddingBlock: 6,
          fontSize: 13,
        },
        sizeSmall: {
          paddingInline: 10,
          paddingBlock: 4,
          fontSize: 12,
        },
        containedPrimary: {
          backgroundColor: blurpleMain,
          boxShadow: '0 1px 2px rgba(58, 54, 184, 0.20)',
          '&:hover': {
            backgroundColor: blurpleDark,
            boxShadow: '0 2px 6px rgba(58, 54, 184, 0.28)',
          },
        },
        outlinedPrimary: {
          borderColor: borderSoft,
          color: textPrimary,
          backgroundColor: paperBg,
          '&:hover': {
            borderColor: blurpleMain,
            backgroundColor: blurpleSoft,
            color: blurpleMain,
          },
        },
        textPrimary: {
          color: blurpleMain,
          '&:hover': {
            backgroundColor: blurpleSoft,
          },
        },
      },
    },

    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },

    // Tabs — flat 2px indicator
    MuiTabs: {
      styleOverrides: {
        root: {
          minHeight: 40,
          borderBottom: `1px solid ${borderSoft}`,
        },
        indicator: {
          height: 2,
          backgroundColor: blurpleMain,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          minHeight: 40,
          textTransform: 'none',
          fontWeight: 500,
          fontSize: 13,
          color: textSecondary,
          paddingInline: 14,
          '&.Mui-selected': {
            color: blurpleMain,
          },
        },
      },
    },

    // Cards / surfaces — flat with 1px border
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundImage: 'none',
        },
        outlined: {
          borderColor: borderSoft,
        },
      },
    },

    MuiCard: {
      defaultProps: {
        elevation: 0,
        variant: 'outlined',
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
          border: `1px solid ${borderSoft}`,
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.04)',
        },
      },
    },

    MuiCardHeader: {
      styleOverrides: {
        root: {
          padding: '14px 16px',
        },
        title: {
          fontSize: 15,
          fontWeight: 600,
        },
      },
    },

    MuiCardContent: {
      styleOverrides: {
        root: {
          padding: 16,
          '&:last-child': {
            paddingBottom: 16,
          },
        },
      },
    },

    // Tables — compact, light header
    MuiTable: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottomColor: borderSoft,
          fontSize: 13,
          paddingTop: 10,
          paddingBottom: 10,
        },
        head: {
          backgroundColor: '#F8F9FC',
          color: textSecondary,
          fontWeight: 600,
          fontSize: 12,
          letterSpacing: 0.2,
          textTransform: 'uppercase',
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          '& .MuiTableCell-head': {
            backgroundColor: '#F8F9FC',
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover td': {
            backgroundColor: '#F7F8FC',
          },
        },
      },
    },

    // Default drawer — same family as page background (slide-over modals use Drawer; nav overrides locally).
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: neutralBg,
          color: textPrimary,
          borderRadius: 0,
          backgroundImage: 'none',
        },
      },
    },
    MuiList: {
      defaultProps: {
        dense: true,
      },
    },
    MuiListItemText: {
      styleOverrides: {
        root: {
          margin: '0 !important',
        },
        primary: {
          fontSize: 13,
        },
        secondary: {
          fontSize: 12,
          color: textMuted,
        },
      },
    },

    // Inputs — compact
    MuiTextField: {
      defaultProps: {
        size: 'small',
        variant: 'outlined',
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          backgroundColor: paperBg,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: borderSoft,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#C7CBDB',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: blurpleMain,
            borderWidth: 1,
          },
        },
        input: {
          fontSize: 13,
          '&:-webkit-autofill': {
            WebkitBoxShadow: `0 0 0 1000px ${paperBg} inset`,
            WebkitTextFillColor: textPrimary,
            caretColor: textPrimary,
          },
          '&:-webkit-autofill:hover': {
            WebkitBoxShadow: `0 0 0 1000px ${paperBg} inset`,
            WebkitTextFillColor: textPrimary,
          },
          '&:-webkit-autofill:focus': {
            WebkitBoxShadow: `0 0 0 1000px ${paperBg} inset`,
            WebkitTextFillColor: textPrimary,
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          fontSize: 13,
        },
      },
    },

    // Chips — compact
    MuiChip: {
      defaultProps: {
        size: 'small',
      },
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 6,
        },
      },
      variants: [
        {
          props: {
            variant: 'special',
          },
          style: {
            backgroundColor: sidebarBg,
            color: sidebarTextStrong,
            paddingLeft: 2,
            paddingRight: 2,
          },
        },
      ],
    },

    // Tooltip — neutral
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: '#1F2937',
          fontSize: 12,
        },
        arrow: {
          color: '#1F2937',
        },
      },
    },

    // Dividers
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: borderSoft,
        },
      },
    },
  },
});
